// src/components/ScheduleDisplay.jsx
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Import React Icons
import { HiPlus } from 'react-icons/hi2';
import { HiCheck } from 'react-icons/hi2';
import { HiArrowsPointingOut } from 'react-icons/hi2';
import { HiArrowsPointingIn } from 'react-icons/hi2';
import { HiCalendarDays } from 'react-icons/hi2';
import { HiBuildingOffice } from 'react-icons/hi2';
import { PiStudentBold, PiChalkboardTeacherBold } from 'react-icons/pi';
import AppStatic, { isStudentsBuild, isTeachersBuild, isAdminBuild } from '../config/AppStatic';
import Button from './ui/Button';
import Card from './ui/Card';
// Modal already imported above
import Input from './ui/Input';
import { useSnackbar } from '../contexts/SnackbarContext'; // Import the snackbar hook
import DayTableSimple from './DayTableSimple.tsx';
import DayTableFit from './DayTableFit.tsx';
import ScheduleFilter from './ScheduleFilter';
import WeekNavigator from './WeekNavigator';
import ConflictIndicator from './ConflictIndicator'; // Import the indicator
import ConflictListModal from './ConflictListModal'; // Import the conflict list modal
import AvailabilityOverlay from './AvailabilityOverlay'; // Import overlay
import EditScheduleItemModal from './EditScheduleItemModal'; // Import the new modal component
// ChangeLogModal handled via FloatingActionButtons
import ReviewChangesButton from './ReviewChangesButton'; // Import the reusable review button
import WeekChangeConfirmationModal from './ui/WeekChangeConfirmationModal'; // Import the new week confirmation modal
import HelpIcon from './Documentation/HelpIcon'; // Import the help icon component
import ComplexFilterModal from './ComplexFilterModal'; // Import the complex filter modal
import { useChangeTracking } from '../hooks/useChangeTracking'; // Import the change tracking hook
import FloatingActionButtons from './ClassSchedulerPage/FloatingActionButtons.tsx';
import ScheduleEntryDetailView from './ScheduleEntryDetailView';
import { SCHEDULE_START_MINUTE, SCHEDULE_END_MINUTE, TOTAL_SCHEDULE_MINUTES } from '../config/constants';
import { getUniqueRooms, parseTime, formatTimeFromMinutes, convertToScheduleEntriesLocalHelper, getWeekStartDate, formatWeekLabel, getWeekNumber, formatDayWithNumber } from '../utils/helpers.ts';
import LinearWeekViewDOM from './LinearWeekViewDOM';
import { useScheduleDragAndDrop, KEY_SEPARATOR } from '../hooks/useScheduleDragAndDrop';
import { useActiveDays } from '../hooks/useActiveDays';
import { useDragToCreate } from '../hooks/useDragToCreate'; // Import the new drag-to-create hook
import { getScheduleEntries } from '../services/apiService';
import SchoolHeader from './SchoolHeader';
import ScheduleEntry from '../models/ScheduleEntry.ts';
import FixedDayHeader from './common/FixedDayHeader';
import EventContainer from './EventContainer';
import RoomsOccupancyView from './RoomsOccupancyView';
import { convertToScheduleEntries, convertToPlainObjects, validateWeekSeries } from '../utils/scheduleUtils';
import AssetManager from '../services/AssetManager.ts';
import { availabilityService } from '../services/AvailabilityService.ts';
import AcademicYearService from '../services/AcademicYearService.ts';
import { conflictManager } from '../services/ConflictManager.ts';
import useAcademicYearInit from '../hooks/useAcademicYearInit';
import logger from '../utils/logger';
import { SharedScrollProvider } from '../contexts/SharedScrollContext';
import Modal from './ui/Modal';

const A4_ASPECT_RATIO = 1.41;

// Clean, simple icon components using React Icons
const PlusIcon = () => <HiPlus className="h-6 w-6" />;
const SaveIcon = () => <HiCheck className="h-6 w-6" />;
const ExpandIcon = () => <HiArrowsPointingOut className="h-full w-full" />;
const FitIcon = () => <HiArrowsPointingIn className="h-full w-full" />;

function ScheduleDisplay({ user, teachers, groups, subjects, rooms, /* NEW: filters and include if passed from InstitutionalSchedulePage */ filters: propFilters, include: propInclude, isPublicView = false, onAddTeacher, onAddSubject }) {
    /* PERFORMANCE OPTIMIZATIONS APPLIED:
     * 1. Split massive filtering effect into smaller, focused effects and memoized computations
     * 2. Memoized expensive date/week calculations to prevent recalculation on every render
     * 3. Debounced conflict calculation and holiday loading to prevent excessive API calls
     * 4. Added useCallback to prevent unnecessary re-renders of child components
     * 5. Moved expensive filtering logic to useMemo hooks for better performance
     */

    // Initialize snackbar notifications
    const { showSnackbar, showSuccess, showError, showWarning, showInfo } = useSnackbar();

    // Initialize URL state management
    const location = useLocation();
    const navigate = useNavigate();

    // Institution slug for public links (students/teachers domains)
    const institutionSlug = useMemo(() => {
        if (!isPublicView) return '';
        const parts = (location.pathname || '').split('/').filter(Boolean);
        return parts[0] || '';
    }, [isPublicView, location.pathname]);
    const studentsUrl = useMemo(() => (
        institutionSlug ? `https://students.pi-schedule.com/${institutionSlug}` : 'https://students.pi-schedule.com/'
    ), [institutionSlug]);
    const teachersUrl = useMemo(() => (
        institutionSlug ? `https://teachers.pi-schedule.com/${institutionSlug}` : 'https://teachers.pi-schedule.com/'
    ), [institutionSlug]);

    // Migration modal state for public admin build on mobile
    const [showMigrationModal, setShowMigrationModal] = useState(false);
    useEffect(() => {
        if (!isPublicView || !isAdminBuild()) return;
        const key = `migrationModalShown:${institutionSlug || 'default'}`;
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
        const already = typeof window !== 'undefined' ? sessionStorage.getItem(key) : '1';
        if (isMobile && !already) {
            setShowMigrationModal(true);
            sessionStorage.setItem(key, '1');
        }
    }, [isPublicView, institutionSlug]);

    // Get active days from AssetManager
    const { activeDays, loading: daysLoading } = useActiveDays();

    // --- State variables ---
    const [initialScheduleItems, setInitialScheduleItems] = useState([]);
    const [baseScheduleItems, setBaseScheduleItems] = useState([]);
    const [filteredScheduleItems, setFilteredScheduleItems] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [selectedCourse, setSelectedCourse] = useState('');
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [isFilteredView, setIsFilteredView] = useState(false);

    // Complex filter state
    const [isComplexFilterModalOpen, setIsComplexFilterModalOpen] = useState(false);
    const [complexFilterGroups, setComplexFilterGroups] = useState([]);
    const [complexFilterTeachers, setComplexFilterTeachers] = useState([]);
    const [complexFilterSubjects, setComplexFilterSubjects] = useState([]);
    const [complexFilterRooms, setComplexFilterRooms] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Start with loading true
    const [error, setError] = useState(null);
    const [uniqueRoomsForGrid, setUniqueRoomsForGrid] = useState([]);
    const [dataInitialized, setDataInitialized] = useState(false); // New state to track initial data load

    // Add new state for week series and year
    const [weekSeries, setWeekSeries] = useState('');
    const [currentDate, setCurrentDate] = useState(new Date()); // State for current date

    // New: time view mode and academic periods (semester mode)
    const [timeMode, setTimeMode] = useState('weeks'); // 'weeks' | 'semester' | 'occupancy'
    const [lastCalendarMode, setLastCalendarMode] = useState('weeks'); // remember last non-occupancy mode
    const [periods, setPeriods] = useState([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState(null);
    // Left drawer for primary view selection (Calendar vs Rooms Occupancy)
    const [isLeftDrawerOpen, setIsLeftDrawerOpen] = useState(false);
    const [isLeftDrawerAnimating, setIsLeftDrawerAnimating] = useState(false);

    // --- URL State Management ---
    // This section handles URL parameter synchronization for filter state.
    // URL parameters supported:
    // - ?teacher=TeacherName - Filter by specific teacher
    // - ?groups=Group1,Group2 - Filter by specific groups (comma-separated)
    // - ?course=CourseName - Filter by specific course
    // 
    // Examples:
    // - /institution/schedule?teacher=John%20Doe
    // - /institution/schedule?groups=Class%20A,Class%20B
    // - /institution/schedule?teacher=Jane%20Smith&groups=Advanced%20Class
    //
    // Helper function to parse URL parameters and initialize filter state
    const parseUrlParams = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const teacher = (searchParams.get('teacher') || '').trim();
        const groups = (searchParams.get('groups') || '').trim();
        const course = (searchParams.get('course') || '').trim();

        // Parse groups parameter - can be comma-separated for multiple groups
        const parsedGroups = groups ?
            groups.split(',').map(g => g.trim()).filter(Boolean) : [];

        return { teacher, groups: parsedGroups, course };
    }, [location.search]);

    // Helper function to update URL with current filter state
    const updateUrlWithFilters = useCallback((teacher, groups, course) => {
        const searchParams = new URLSearchParams(location.search);

        // Update or remove parameters based on current values
        if (teacher && teacher.trim()) {
            searchParams.set('teacher', teacher.trim());
        } else {
            searchParams.delete('teacher');
        }

        if (groups && groups.length > 0) {
            // Filter out empty strings and trim whitespace
            const validGroups = groups.filter(g => g && g.trim()).map(g => g.trim());
            if (validGroups.length > 0) {
                searchParams.set('groups', validGroups.join(','));
            } else {
                searchParams.delete('groups');
            }
        } else {
            searchParams.delete('groups');
        }

        if (course && course.trim()) {
            searchParams.set('course', course.trim());
        } else {
            searchParams.delete('course');
        }

        // Update URL without triggering a page reload
        const newSearch = searchParams.toString();
        const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

        // Only update if the URL actually changed to prevent unnecessary navigation
        if (newUrl !== location.pathname + location.search) {
            navigate(newUrl, { replace: true });
        }
    }, [location.pathname, location.search, navigate]);

    // Initialize filter state from URL parameters on mount
    useEffect(() => {
        const urlParams = parseUrlParams();

        // Log URL parameters for debugging
        if (urlParams.teacher || urlParams.groups.length > 0 || urlParams.course) {
            console.log('[ScheduleDisplay] Initializing filters from URL:', urlParams);
        }

        // Always respect URL parameters on initial load
        // This allows direct URL access to filtered views
        if (urlParams.teacher !== selectedTeacher) {
            setSelectedTeacher(urlParams.teacher);
        }
        if (JSON.stringify(urlParams.groups) !== JSON.stringify(selectedGroups)) {
            setSelectedGroups(urlParams.groups);
        }
        if (urlParams.course !== selectedCourse) {
            setSelectedCourse(urlParams.course);
        }
    }, []); // Only run on mount

    // Persist last-used public teacher/group filter and restore when visiting without query params
    const lastPublicFilterKey = useMemo(() => `publicLastLinearFilter:${location.pathname}`, [location.pathname]);

    // Restore saved filter on initial load when in public view and no URL filters are present
    useEffect(() => {
        if (!isPublicView) return;

        const urlParams = parseUrlParams();
        const noUrlFilters = !urlParams.teacher && (!urlParams.groups || urlParams.groups.length === 0) && !urlParams.course;

        if (!noUrlFilters) return;

        try {
            const raw = localStorage.getItem(lastPublicFilterKey);
            if (!raw) return;
            const saved = JSON.parse(raw);
            const savedTeacher = (saved?.teacher || '').trim();
            const savedGroups = Array.isArray(saved?.groups) ? saved.groups.filter(Boolean) : [];

            if (savedTeacher || savedGroups.length > 0) {
                // Update state and URL to reflect saved filter
                if (savedTeacher !== selectedTeacher) setSelectedTeacher(savedTeacher);
                if (JSON.stringify(savedGroups) !== JSON.stringify(selectedGroups)) setSelectedGroups(savedGroups);
                if (selectedCourse) setSelectedCourse('');
                updateUrlWithFilters(savedTeacher, savedGroups, '');
            }
        } catch (_) {
            // ignore JSON parse errors
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPublicView, parseUrlParams, updateUrlWithFilters, lastPublicFilterKey]);

    // Save or clear last-used filter whenever teacher/groups change in public view
    useEffect(() => {
        if (!isPublicView) return;

        const hasTeacher = !!(selectedTeacher && selectedTeacher.trim());
        const hasGroups = Array.isArray(selectedGroups) && selectedGroups.length > 0;

        if (hasTeacher || hasGroups) {
            const payload = {
                teacher: hasTeacher ? selectedTeacher.trim() : '',
                groups: hasGroups ? selectedGroups.filter(Boolean) : []
            };
            try {
                localStorage.setItem(lastPublicFilterKey, JSON.stringify(payload));
            } catch (_) { /* no-op */ }
        } else {
            try {
                localStorage.removeItem(lastPublicFilterKey);
            } catch (_) { /* no-op */ }
        }
    }, [isPublicView, selectedTeacher, selectedGroups, lastPublicFilterKey]);

    // --- Event modal state (moved to EventContainer) ---
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);
    const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
    const [viewMode, setViewMode] = useState('wide');
    const [fitViewHeight, setFitViewHeight] = useState(600); // Default height
    const [currentConflicts, setCurrentConflicts] = useState([]);
    const [isConflictListVisible, setIsConflictListVisible] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Initialize change tracking hook
    const {
        changeLog,
        addLogEntry,
        calculateNetChanges,
        initializeChangeTracking,
        getNetChanges,
        isChangeLogModalOpen,
        openChangeLogModal,
        closeChangeLogModal,
    } = useChangeTracking(user);

    // Track unsaved changes for warnings
    const hasUnsavedChanges = useMemo(() => Array.isArray(changeLog) && changeLog.length > 0, [changeLog.length]);

    // Warn if leaving/reloading the page with unsaved changes
    useEffect(() => {
        const beforeUnloadHandler = (e) => {
            if (!hasUnsavedChanges) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', beforeUnloadHandler);
        return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
    }, [hasUnsavedChanges]);

    // Block in-app navigation when there are unsaved changes (without data router)
    useEffect(() => {
        if (!hasUnsavedChanges) return;

        const shouldIntercept = (anchor) => {
            if (!anchor || !anchor.href) return false;
            try {
                const url = new URL(anchor.href, window.location.origin);
                return url.origin === window.location.origin && url.pathname !== window.location.pathname;
            } catch (_) { return false; }
        };

        const onClick = (e) => {
            // Only left-clicks without modifier keys
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            let el = e.target;
            while (el && el !== document.body) {
                if (el.tagName === 'A' && shouldIntercept(el)) {
                    const ok = window.confirm('You have unsaved changes. If you navigate away, they will be lost. Continue?');
                    if (!ok) e.preventDefault();
                    return;
                }
                el = el.parentElement;
            }
        };

        const onPopState = (e) => {
            const ok = window.confirm('You have unsaved changes. If you navigate away, they will be lost. Continue?');
            if (!ok) {
                // Push back to current location to cancel
                window.history.pushState(null, '', window.location.href);
            }
        };

        document.addEventListener('click', onClick, true);
        window.addEventListener('popstate', onPopState);
        return () => {
            document.removeEventListener('click', onClick, true);
            window.removeEventListener('popstate', onPopState);
        };
    }, [hasUnsavedChanges]);

    // Initialize the drag-to-create hook
    const {
        dragToCreateData,
        handleDragToCreateStart,
        handleDragToCreateMove,
        handleDragToCreateEnd,
        handleDragToCreateCancel
    } = useDragToCreate(user?.role === 'Admin', {
        onModalCreate: true, // Use modal creation behavior for ScheduleDisplay
        setSelectedScheduleItem,
        setIsScheduleModalOpen,
        currentYear: currentDate.getFullYear(),
        currentDate: currentDate,
        rooms: rooms // Pass rooms array for room name lookup
    });

    // --- Memoized computed values to prevent recalculation ---
    const targetWeekNumber = useMemo(() => getWeekNumber(currentDate), [currentDate]);
    const targetYear = useMemo(() => currentDate.getFullYear(), [currentDate]);
    const currentWeekLabel = useMemo(() => formatWeekLabel(getWeekStartDate(currentDate)), [currentDate]);

    // --- Derived values (computed from state) ---
    const isAdmin = user?.role === 'Admin';

    // Memoize the expensive exception processing with early returns
    const entriesWithExceptions = useMemo(() => {
        if (!dataInitialized) {
            return [];
        }

        const safeBaseItems = Array.isArray(baseScheduleItems) ? baseScheduleItems : [];
        if (safeBaseItems.length === 0) {
            return [];
        }

        const validItems = safeBaseItems.filter(item => !!item);
        if (validItems.length === 0) {
            return [];
        }

        logger.debug(`[ScheduleDisplay] Processing ${validItems.length} entries for Year: ${targetYear}, Week: ${targetWeekNumber}`);

        // Apply exceptions and get the correct entry state for the current week
        const result = validItems.map(entry => {
            if (entry && typeof entry.getEntryForWeek === 'function') {
                return entry.getEntryForWeek(targetWeekNumber, targetYear);
            }
            return entry;
        }).filter(Boolean); // Remove null entries (cancelled for the week)

        console.log(`[ScheduleDisplay] After exception processing: ${result.length} entries remain`);
        return result;
    }, [baseScheduleItems, targetWeekNumber, targetYear, dataInitialized]);

    // Memoize the week-active filtering with optimizations
    const weekActiveEntries = useMemo(() => {
        if (!entriesWithExceptions || entriesWithExceptions.length === 0) {
            return [];
        }

        const filteredEntries = entriesWithExceptions.filter(entry => {
            if (!entry) return false;

            const targetWeekActive = entry.isActiveForWeekSync
                ? entry.isActiveForWeekSync(targetWeekNumber, targetYear)
                : (entry.isActiveForWeek
                    ? entry.isActiveForWeek(targetWeekNumber, targetYear)
                    : true);

            if (!targetWeekActive) {
                return false;
            }

            return true;
        });

        console.log(`[ScheduleDisplay] Week filtering: ${entriesWithExceptions.length} -> ${filteredEntries.length} entries`);
        return filteredEntries;
    }, [entriesWithExceptions, targetWeekNumber, targetYear]);

    // Selected period for semester mode
    const selectedPeriod = useMemo(() => {
        if (!periods || periods.length === 0 || !selectedPeriodId) return null;
        return periods.find(p => String(p.id) === String(selectedPeriodId)) || null;
    }, [periods, selectedPeriodId]);

    // Entries visible in semester mode: any entry active in the selected period
    const semesterEntries = useMemo(() => {
        if (timeMode !== 'semester' || !selectedPeriod) return [];

        const source = Array.isArray(baseScheduleItems) ? baseScheduleItems : [];
        const minYear = selectedPeriod.year || currentDate.getFullYear();
        const maxYear = selectedPeriod.end_week < selectedPeriod.start_week ? (minYear + 1) : minYear;

        const result = source.filter(entry => {
            if (!entry) return false;
            // Unscheduled entries (no week series) are included
            if (typeof entry.isScheduled === 'function' && !entry.isScheduled()) return true;
            try {
                const weeks = typeof entry.getActiveWeeksSync === 'function' ? entry.getActiveWeeksSync(minYear, maxYear) : [];
                if (weeks && weeks.length > 0) {
                    return weeks.some(wk => typeof selectedPeriod.containsWeek === 'function' && selectedPeriod.containsWeek(wk.week, wk.year));
                }
            } catch (_) { /* no-op */ }
            return false;
        });

        console.log(`[ScheduleDisplay] Semester filtering: ${source.length} -> ${result.length} entries`);
        return result;
    }, [timeMode, selectedPeriod, baseScheduleItems, currentDate]);

    // Base entries for subsequent user filters depending on mode
    const timeModeBaseEntries = useMemo(() => {
        if (timeMode === 'semester' && selectedPeriod) return semesterEntries;
        return weekActiveEntries;
    }, [timeMode, selectedPeriod, semesterEntries, weekActiveEntries]);

    // Split filtering effect into smaller, focused effects
    // Effect 1: Handle filter view mode
    useEffect(() => {
        const teacherIsSelected = !!selectedTeacher;
        const safeSelectedGroups = Array.isArray(selectedGroups) ? selectedGroups : [];
        const groupsAreSelected = safeSelectedGroups.length > 0;

        // Check if complex filters are active
        const complexFiltersActive = complexFilterGroups.length > 0 ||
            complexFilterTeachers.length > 0 ||
            complexFilterSubjects.length > 0 ||
            complexFilterRooms.length > 0;

        const shouldShowFilteredView = teacherIsSelected || (isPublicView && groupsAreSelected) || complexFiltersActive;
        setIsFilteredView(shouldShowFilteredView);
    }, [selectedTeacher, selectedGroups, isPublicView, complexFilterGroups, complexFilterTeachers, complexFilterSubjects, complexFilterRooms]);

    // Effect 2: Apply user filters (teacher, course, groups) - optimized
    const userFilteredEntries = useMemo(() => {
        if (!timeModeBaseEntries || timeModeBaseEntries.length === 0) {
            return [];
        }

        const teacherIsSelected = !!selectedTeacher;
        const safeSelectedGroups = Array.isArray(selectedGroups) ? selectedGroups : [];
        const groupsAreSelected = safeSelectedGroups.length > 0;

        // Check if complex filters are active
        const complexFiltersActive = complexFilterGroups.length > 0 ||
            complexFilterTeachers.length > 0 ||
            complexFilterSubjects.length > 0 ||
            complexFilterRooms.length > 0;

        // If no filters are applied, return all week-active entries
        if (!teacherIsSelected && !selectedCourse && !groupsAreSelected && !complexFiltersActive) {
            console.log(`[ScheduleDisplay] No filters applied, returning ${timeModeBaseEntries.length} entries`);
            return timeModeBaseEntries;
        }

        const filteredEntries = timeModeBaseEntries.filter(entry => {
            if (!entry) return false;

            // Simple filters (legacy)
            const teacherMatch = !selectedTeacher || (
                typeof entry.hasTeacher === 'function'
                    ? entry.hasTeacher(selectedTeacher)
                    : (
                        entry.teacher === selectedTeacher || (
                            Array.isArray(entry.teachers) && entry.teachers.some(t => (t && t.name) === selectedTeacher)
                        )
                    )
            );
            const courseMatch = !selectedCourse || entry.course === selectedCourse;
            const groupMatch = !groupsAreSelected || entry.includesGroup(safeSelectedGroups);

            // Complex filters
            let complexGroupMatch = true;
            let complexTeacherMatch = true;
            let complexSubjectMatch = true;

            if (complexFilterGroups.length > 0) {
                complexGroupMatch = entry.includesGroup(complexFilterGroups);
            }

            if (complexFilterTeachers.length > 0) {
                complexTeacherMatch = typeof entry.hasTeacher === 'function'
                    ? complexFilterTeachers.some(name => entry.hasTeacher(name))
                    : (() => {
                        const entryTeacherNames = [];
                        if (Array.isArray(entry.teachers)) {
                            entryTeacherNames.push(...entry.teachers.map(t => t && t.name).filter(Boolean));
                        }
                        if (entry.teacher) {
                            entryTeacherNames.push(entry.teacher);
                        }
                        return entryTeacherNames.some(name => complexFilterTeachers.includes(name));
                    })();
            }

            if (complexFilterSubjects.length > 0) {
                complexSubjectMatch = complexFilterSubjects.includes(entry.course);
            }

            // Complex room filter
            let complexRoomMatch = true;
            if (complexFilterRooms.length > 0) {
                const entryRoom = entry.room || '';
                complexRoomMatch = complexFilterRooms.includes(entryRoom);
            }

            // Apply all filters: simple filters AND complex filters
            return teacherMatch && courseMatch && groupMatch &&
                complexGroupMatch && complexTeacherMatch && complexSubjectMatch && complexRoomMatch;
        });

        console.log(`[ScheduleDisplay] User filtering: ${timeModeBaseEntries.length} -> ${filteredEntries.length} entries`);
        return filteredEntries;
    }, [timeModeBaseEntries, selectedTeacher, selectedCourse, selectedGroups, complexFilterGroups, complexFilterTeachers, complexFilterSubjects, complexFilterRooms]);

    // Effect 3: Update filtered items and unique rooms - with change detection
    const lastFilteredItemsRef = useRef(null);

    useEffect(() => {
        // Create a signature to detect actual changes - include key properties that affect visual rendering
        const filteredSignature = userFilteredEntries.map(item =>
            `${item?.id}-${item?.day}-${item?.timeStart}-${item?.timeEnd}-${item?.room}-${item?.teacher}-${item?.course}`
        ).join('|');

        // Only update if the items have actually changed
        if (lastFilteredItemsRef.current !== filteredSignature) {
            console.log(`[ScheduleDisplay] Updating filtered items: ${userFilteredEntries.length} entries`);
            setFilteredScheduleItems(userFilteredEntries);

            // Update unique rooms based on filtered items
            const currentRooms = getUniqueRooms(userFilteredEntries);
            setUniqueRoomsForGrid(currentRooms);

            lastFilteredItemsRef.current = filteredSignature;
        }
    }, [userFilteredEntries]);

    // --- Refs ---
    const assetManager = useMemo(() => AssetManager.getInstance(), []);
    const academicYearService = useMemo(() => AcademicYearService.getInstance(), []);
    const fitViewContainerRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const dayHeaderRefs = useRef({});
    const [currentVisibleDay, setCurrentVisibleDay] = useState(activeDays[0]);
    const [previousDay, setPreviousDay] = useState(activeDays[0]);

    // --- Holiday State ---
    const [weekHolidays, setWeekHolidays] = useState(new Map()); // Map<string, Holiday | null> - key is day name, value is Holiday object from API

    // Main effect to fetch and process schedule data - only run once on mount
    useEffect(() => {
        let isMounted = true;

        const fetchEntries = async () => {
            if (!isMounted) return;

            try {
                console.log('[ScheduleDisplay] Fetching schedule entries using AssetManager.');
                const currentFilters = propFilters || {};
                const currentInclude = propInclude || '';

                // Potentially, AssetManager could accept year/week to fetch only relevant data
                // For now, it fetches all, and we filter client-side
                const scheduleEntries = await assetManager.fetchScheduleEntries(currentFilters, currentInclude);
                console.log('[ScheduleDisplay] Received ScheduleEntry objects from AssetManager:', scheduleEntries?.length || 0);

                if (!isMounted) return;

                if (scheduleEntries) {
                    // Calculate conflicts for all entries before setting state
                    console.log('[ScheduleDisplay] Calculating conflicts for loaded entries...');
                    const currentWeekNumber = getWeekNumber(currentDate);
                    const currentYear = currentDate.getFullYear();

                    const entriesWithConflicts = await conflictManager.calculateConflictsForEntries(
                        scheduleEntries,
                        teachers,
                        groups,
                        subjects
                    );

                    setInitialScheduleItems(entriesWithConflicts);
                    setBaseScheduleItems(entriesWithConflicts);
                    // Initial filtering will happen in the other useEffect
                    // Initialize change tracking with the loaded data
                    initializeChangeTracking(entriesWithConflicts);
                } else {
                    setInitialScheduleItems([]);
                    setBaseScheduleItems([]);
                    setFilteredScheduleItems([]);
                    initializeChangeTracking([]);
                }
                setDataInitialized(true);
            } catch (err) {
                if (!isMounted) return;
                console.error('[ScheduleDisplay] Error fetching schedule entries via AssetManager:', err);
                setError(err.message || 'Failed to load schedule entries.');
                setInitialScheduleItems([]);
                setBaseScheduleItems([]);
                setFilteredScheduleItems([]);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchEntries();

        return () => {
            isMounted = false;
        };
    }, [assetManager, propFilters, propInclude]); // Removed currentDate from dependencies, so changing weeks won't trigger a fetch

    // Load periods when switching to Semester or Occupancy mode
    useEffect(() => {
        let isMounted = true;
        const loadPeriods = async () => {
            if (timeMode !== 'semester' && timeMode !== 'occupancy') return;
            try {
                const fetched = await assetManager.fetchPeriods();
                if (!isMounted) return;
                const safe = Array.isArray(fetched) ? fetched : [];
                setPeriods(safe);
                if (safe.length > 0) {
                    const wk = getWeekNumber(currentDate);
                    const yr = currentDate.getFullYear();
                    const match = safe.find(p => typeof p.containsWeek === 'function' ? p.containsWeek(wk, yr) : false) || safe[0];
                    if (!selectedPeriodId || !safe.some(p => String(p.id) === String(selectedPeriodId))) {
                        setSelectedPeriodId(match?.id ?? safe[0].id);
                    }
                }
            } catch (e) {
                console.warn('[ScheduleDisplay] Failed to fetch periods:', e);
                setPeriods([]);
            }
        };
        loadPeriods();
        return () => { isMounted = false; };
    }, [timeMode, assetManager, currentDate, selectedPeriodId]);
    // Debounced effect to load holiday information for the current week
    useEffect(() => {
        let isMounted = true;
        let timeoutId;

        const loadHolidays = async () => {
            try {
                console.log(`[ScheduleDisplay] Loading holidays for week ${targetWeekNumber}, year ${targetYear}`);

                const holidayMap = new Map();

                // Check each day of the week for holidays
                for (const day of activeDays) {
                    try {
                        const holiday = await academicYearService.getHolidayForDay(day, targetWeekNumber, targetYear);
                        holidayMap.set(day, holiday);

                        if (holiday) {
                            console.log(`[ScheduleDisplay] Found holiday on ${day}: ${holiday.name} (${holiday.type})`);
                        }
                    } catch (error) {
                        console.warn(`[ScheduleDisplay] Error checking holiday for ${day}:`, error);
                        holidayMap.set(day, null);
                    }
                }

                if (isMounted) {
                    setWeekHolidays(holidayMap);
                }
            } catch (error) {
                console.error('[ScheduleDisplay] Error loading holidays:', error);
                if (isMounted) {
                    setWeekHolidays(new Map());
                }
            }
        };

        // Debounce holiday loading to prevent excessive API calls
        timeoutId = setTimeout(loadHolidays, 150);

        return () => {
            isMounted = false;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [targetWeekNumber, targetYear, academicYearService]);

    // Effect to calculate net changes when schedule items change - with guards
    const lastChangeCalculationRef = useRef(null);

    useEffect(() => {
        // Create a signature of the current items to prevent unnecessary recalculations
        const itemsSignature = baseScheduleItems.map(item =>
            `${item?.id}-${item?.day}-${item?.timeStart}-${item?.timeEnd}-${item?.room}-${item?.teacher}-${item?.course}`
        ).join('|');

        // Only calculate if the items have actually changed
        if (lastChangeCalculationRef.current !== itemsSignature) {
            console.log("Calculating net changes...");
            calculateNetChanges(baseScheduleItems);
            lastChangeCalculationRef.current = itemsSignature;
        }
    }, [baseScheduleItems, calculateNetChanges]);

    // --- MODIFIED: Centralized Drag State ---
    // const [draggedItemInfo, setDraggedItemInfo] = useState({ id: null, clickOffsetY: 0, durationMinutes: 0 });
    // const [availabilityOverlayData, setAvailabilityOverlayData] = useState(null); // For pre-calculated overlays (optional but can optimize)
    const KEY_SEPARATOR = '|'; // Define separator for overlay map keys

    // Initialize the drag and drop hook
    const {
        draggedItemInfo,
        availabilityOverlayData,
        handleGlobalDragStart,
        handleGlobalDragEnd,
        handleItemDrop,
        // Week confirmation modal state and handlers
        isWeekConfirmationModalOpen,
        pendingMoveData,
        handleWeekConfirmationClose,
        handleWeekConfirmationConfirm,
    } = useScheduleDragAndDrop(baseScheduleItems, setBaseScheduleItems, user, addLogEntry, teachers, groups, subjects, rooms, { currentDate, activeDays });

    // --- Resize Handler ---
    const handleItemResize = useCallback(async (itemId, newStartTime, newEndTime) => {
        logger.debug(`[ScheduleDisplay] handleItemResize called:`, {
            itemId,
            newStartTime,
            newEndTime
        });

        // Find the item to resize
        const itemToResize = baseScheduleItems.find(item => item.id === itemId);
        if (!itemToResize) {
            console.error(`[ScheduleDisplay] Item not found for resize: ${itemId}`);
            return;
        }

        // Create updated item with new times
        const updatedItem = itemToResize.clone();
        updatedItem.timeStart = newStartTime;
        updatedItem.timeEnd = newEndTime;

        // Update the schedule instances
        const updatedInstances = baseScheduleItems.map(item =>
            item.id === itemId ? updatedItem : item
        );

        // Update state immediately for responsive UI (Requirement 8.4)
        setBaseScheduleItems(updatedInstances);

        // Add to change tracking log (Requirement 8.3)
        addLogEntry('edit', itemToResize, updatedItem);

        // Recalculate conflicts for the modified entry and affected entries (Requirements 7.1, 7.2, 7.3)
        try {
            const entriesWithUpdatedConflicts = await conflictManager.recalculateConflictsForChangedEntries(
                [itemId],
                updatedInstances,
                teachers,
                groups,
                subjects
            );

            // Update state with conflict information (Requirements 7.4, 7.5)
            setBaseScheduleItems(entriesWithUpdatedConflicts);

            logger.debug(`[ScheduleDisplay] Resize completed with conflict recalculation for item: ${itemId}`);
        } catch (error) {
            console.error(`[ScheduleDisplay] Error recalculating conflicts after resize:`, error);
            // Keep the time changes even if conflict calculation fails
        }
    }, [baseScheduleItems, teachers, groups, subjects, addLogEntry]);

    // Function to determine animation direction
    const getAnimationDirection = useCallback(() => {
        const currentIndex = activeDays.indexOf(currentVisibleDay);
        const previousIndex = activeDays.indexOf(previousDay);
        return currentIndex > previousIndex ? 'slideUp' : 'slideDown';
    }, [currentVisibleDay, previousDay, activeDays]);

    // Add throttled scroll event listener - optimized version
    useEffect(() => {
        // Only set up scroll listener after loading is complete
        if (isLoading) return;

        let ticking = false;

        // Create a throttled scroll handler to improve performance
        const scrollHandler = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    if (!scrollContainerRef.current) {
                        ticking = false;
                        return;
                    }

                    const scrollTop = scrollContainerRef.current.scrollTop;
                    let foundDay = activeDays[0]; // Default to the first day

                    // Iterate through day headers to find the one currently at the top
                    for (const day of activeDays) {
                        const headerElement = dayHeaderRefs.current[day];
                        if (headerElement) {
                            const offsetTop = headerElement.offsetTop;
                            const headerHeight = headerElement.offsetHeight;
                            const activationOffset = headerHeight * 4;

                            if (scrollTop >= offsetTop - activationOffset) {
                                foundDay = day;
                            } else {
                                break;
                            }
                        }
                    }

                    // Use functional updates to avoid stale closures
                    setCurrentVisibleDay(currentDay => {
                        if (foundDay !== currentDay) {
                            setPreviousDay(currentDay);
                            return foundDay;
                        }
                        return currentDay;
                    });

                    ticking = false;
                });
                ticking = true;
            }
        };

        // Add listener with a delay to ensure DOM is ready
        const timer = setTimeout(() => {
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', scrollHandler);
                scrollHandler(); // Initial call to set correct day
            } else {
                console.warn('[ScheduleDisplay] scrollContainer ref is still null after loading completed');
            }
        }, 100); // Increased delay to ensure DOM is fully ready

        return () => {
            clearTimeout(timer);
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', scrollHandler);
            }
        };
    }, [isLoading]); // Depend on isLoading to set up listener after loading completes

    // --- Effects ---
    // useEffect(() => {
    //     console.log("User Role:", user?.role);
    //     setFilteredScheduleItems(Array.isArray(baseScheduleItems) ? baseScheduleItems : []);
    // }, [user, baseScheduleItems]);

    const toggleViewMode = () => {
        setViewMode(prevMode => (prevMode === 'wide' ? 'fit' : 'wide'));
        if (hasUnsavedChanges) {
            showWarning('You have unsaved changes. Reloading or leaving the page will discard them.');
        }
    };

    useEffect(() => {
        if (viewMode !== 'fit') return;
        const element = fitViewContainerRef.current;
        if (!element) return;
        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const newWidth = entry.contentRect.width;
                if (newWidth > 0) {
                    setFitViewHeight(newWidth * A4_ASPECT_RATIO);
                }
            }
        });
        resizeObserver.observe(element);
        const initialWidth = element.getBoundingClientRect().width;
        if (initialWidth > 0) {
            setFitViewHeight(initialWidth * A4_ASPECT_RATIO);
        }
        return () => resizeObserver.disconnect();
    }, [viewMode]);

    // --- Modal Handlers ---
    const openScheduleModal = useCallback((item) => {
        setSelectedScheduleItem(item);
        if (isPublicView) {
            setIsDetailViewOpen(true);
        } else {
            setIsScheduleModalOpen(true);
        }
    }, [isPublicView]);
    const closeScheduleModal = useCallback(() => {
        setIsScheduleModalOpen(false);
        setSelectedScheduleItem(null);
    }, []);

    const closeDetailView = useCallback(() => {
        setIsDetailViewOpen(false);
        setSelectedScheduleItem(null);
    }, []);

    // --- Context Menu: Duplicate ---
    const handleDuplicateItem = useCallback((item) => {
        try {
            const sourceObj = typeof item?.toObject === 'function' ? item.toObject() : item;
            const newItemId = `item-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
            const newEntry = new ScheduleEntry({ ...sourceObj, id: newItemId });

            setBaseScheduleItems(prev => {
                const updated = [...prev, newEntry];
                conflictManager.recalculateConflictsForChangedEntries(
                    [newEntry.id],
                    updated,
                    teachers,
                    groups,
                    subjects
                ).then(withConflicts => setBaseScheduleItems(withConflicts));
                return updated;
            });

            addLogEntry('create', null, newEntry);
        } catch (e) {
            console.error('[ScheduleDisplay] Duplicate failed:', e);
        }
    }, [teachers, groups, subjects]);

    // --- Context Menu: Delete ---
    const handleDeleteItem = useCallback((item) => {
        console.log('[ScheduleDisplay] Deleting item:', item.id);

        setBaseScheduleItems(prev =>
            prev.filter(entry => entry.id !== item.id)
        );

        addLogEntry('delete', item, null);
        showSuccess('Schedule item deleted successfully');
    }, [addLogEntry, showSuccess]);


    // --- Filter Change Handlers ---
    const handleTeacherChange = useCallback((value) => {
        setSelectedTeacher(value);
        if (value) {
            setSelectedCourse('');
            setSelectedGroups([]);
        }
        // Update URL with new filter state
        updateUrlWithFilters(value, [], '');
    }, [updateUrlWithFilters]);
    const handleCourseChange = useCallback((value) => {
        setSelectedCourse(value);
        if (value) {
            setSelectedTeacher('');
            setSelectedGroups([]);
        }
        // Update URL with new filter state
        updateUrlWithFilters('', [], value);
    }, [updateUrlWithFilters]);
    const handleGroupChange = useCallback((selectedGroupsArray) => {
        let groupsToSet = [];

        if (isPublicView) {
            // In public view, expect a single value (string) and convert to array
            groupsToSet = selectedGroupsArray ? [selectedGroupsArray] : [];
            setSelectedGroups(groupsToSet);
            if (groupsToSet.length > 0) {
                setSelectedTeacher('');
                setSelectedCourse('');
            }
        } else {
            // In admin view, expect an array
            groupsToSet = Array.isArray(selectedGroupsArray) ? selectedGroupsArray : [];
            setSelectedGroups(groupsToSet);
            if (groupsToSet.length > 0) {
                setSelectedTeacher('');
                setSelectedCourse('');
            }
        }

        // Update URL with new filter state
        updateUrlWithFilters('', groupsToSet, '');
    }, [isPublicView, updateUrlWithFilters]);
    const clearFilters = useCallback(() => {
        setSelectedTeacher('');
        setSelectedCourse('');
        setSelectedGroups([]);
        // Also clear complex filters
        setComplexFilterGroups([]);
        setComplexFilterTeachers([]);
        setComplexFilterSubjects([]);
        setComplexFilterRooms([]);

        // Update URL to remove all filter parameters
        updateUrlWithFilters('', [], '');
    }, [updateUrlWithFilters]);

    // Complex filter handlers
    const handleOpenComplexFilter = useCallback(() => {
        setIsComplexFilterModalOpen(true);
    }, []);

    const handleCloseComplexFilter = useCallback(() => {
        setIsComplexFilterModalOpen(false);
    }, []);

    const handleApplyComplexFilters = useCallback((filters) => {
        setComplexFilterGroups(filters.groups || []);
        setComplexFilterTeachers(filters.teachers || []);
        setComplexFilterSubjects(filters.subjects || []);
        setComplexFilterRooms(filters.rooms || []);

        // Update URL with complex filter state
        // For complex filters, we'll use the first teacher/subject if multiple are selected
        // and combine with groups
        const primaryTeacher = filters.teachers && filters.teachers.length > 0 ? filters.teachers[0] : '';
        const primarySubject = filters.subjects && filters.subjects.length > 0 ? filters.subjects[0] : '';
        const combinedGroups = [...(filters.groups || [])];

        updateUrlWithFilters(primaryTeacher, combinedGroups, primarySubject);
    }, [updateUrlWithFilters]);

    // --- Add/Edit/Drop Handlers ---
    const handleAddNewItemClick = () => {
        // Create a template schedule item for adding
        const templateItem = new ScheduleEntry({
            id: `temp-add-${Date.now()}`,
            day: activeDays[0],
            timeStart: '09:00',
            timeEnd: '10:30',
            course: '',
            teacher: '',
            groups: [],
            room: '',
            type: 'class',
            weekSeries: '',
            year: currentDate.getFullYear()
        });
        setSelectedScheduleItem(templateItem);
        setIsScheduleModalOpen(true);
    };


    // Helper function to check conflict status of an entry
    const getConflictStatusForEntry = useCallback((scheduleEntry) => {
        if (!scheduleEntry || scheduleEntry.type === 'event') {
            return 'green'; // Events don't have conflicts
        }

        try {
            const entryWithConflicts = conflictManager.calculateConflictForEntry(
                scheduleEntry,
                baseScheduleItems,
                teachers,
                groups,
                subjects
            );

            // Get overall conflict status
            if (entryWithConflicts && typeof entryWithConflicts.getOverallConflictStatus === 'function') {
                return entryWithConflicts.getOverallConflictStatus();
            }

            return 'green';
        } catch (error) {
            console.error('[ScheduleDisplay] Error checking conflict status:', error);
            return 'green';
        }
    }, [baseScheduleItems, teachers, groups, subjects]);

    // Helper to format conflict reasons: collapse identical reasons across all active weeks to "all weeks"
    const formatConflictReasonsForEntry = useCallback((availabilityObj, entry) => {
        const reasons = availabilityObj?.conflictReasons || [];
        if (!entry || reasons.length === 0) return [];

        // Determine years present in conflicts
        const yearsInConflicts = new Set(
            reasons.map(r => r?.year).filter(y => typeof y === 'number')
        );

        // Build set of active week keys ("week-year") for the entry, limited to the years present in conflicts
        let activeWeekKeys = new Set();
        if (yearsInConflicts.size > 0) {
            const yearsArray = Array.from(yearsInConflicts);
            const minYear = Math.min(...yearsArray);
            const maxYear = Math.max(...yearsArray);
            try {
                const activeWeeks = entry.getActiveWeeksSync(minYear, maxYear);
                activeWeekKeys = new Set(
                    activeWeeks
                        .filter(w => yearsInConflicts.has(w.year))
                        .map(w => `${w.week}-${w.year}`)
                );
            } catch (e) {
                // Fallback: if anything goes wrong, don't try to collapse
                activeWeekKeys = new Set();
            }
        }

        // Group conflicts by reason text
        const groupedByReason = new Map();
        for (const r of reasons) {
            const reasonText = r?.reason || '';
            if (!groupedByReason.has(reasonText)) groupedByReason.set(reasonText, new Set());
            const wk = (typeof r?.week === 'number' && typeof r?.year === 'number')
                ? `${r.week}-${r.year}`
                : null;
            if (wk) groupedByReason.get(reasonText).add(wk);
        }

        // Determine which reasons should be collapsed to "all weeks"
        const collapseToAllWeeks = new Set();
        if (activeWeekKeys.size > 1) {
            groupedByReason.forEach((weekKeys, reasonText) => {
                // Collapse only if the reason appears for every active week in the years observed
                if (
                    weekKeys.size === activeWeekKeys.size &&
                    [...weekKeys].every(k => activeWeekKeys.has(k))
                ) {
                    collapseToAllWeeks.add(reasonText);
                }
            });
        }

        // Build final list of lines to display in the confirm dialog
        const lines = [];
        // 1) Collapsed reasons once with "(all weeks)"
        collapseToAllWeeks.forEach(reasonText => {
            lines.push(`${reasonText} (all weeks)`);
        });
        // 2) For other reasons, keep original duplication as-is
        reasons.forEach(r => {
            if (!collapseToAllWeeks.has(r?.reason || '')) {
                lines.push(r?.reason || '');
            }
        });
        // Remove empty lines just in case
        return lines.filter(Boolean);
    }, []);

    // Modified handler for EditScheduleItemModal that handles both add and edit
    const handleSaveEdit = useCallback((editedDataObj, originalItem) => {
        // Check if this is an add operation (temporary ID) or edit operation
        const isAddOperation = originalItem && originalItem.id && String(originalItem.id).startsWith('temp-add-');

        if (isAddOperation) {
            // This is an add operation - create new item with new ID
            const newItemId = `item-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
            const newItemData = {
                ...editedDataObj,
                id: newItemId
            };
            const newEntry = new ScheduleEntry(newItemData);

            // Optional: Check availability before adding
            const startMinutes = newEntry.startMinutes;
            if (startMinutes !== null) {
                const currentWeekNumber = getWeekNumber(currentDate);

                const addAvailability = availabilityService.checkAvailability(
                    newEntry, newEntry.room, newEntry.day, startMinutes,
                    baseScheduleItems, teachers, groups, subjects
                );
                if (addAvailability.overallStatus === 'red') {
                    const formattedLines = formatConflictReasonsForEntry(addAvailability, newEntry);
                    const conflictDetails = formattedLines.join('\n - ');
                    const confirmAdd = window.confirm(`Warning: Adding this item creates conflicts in week ${currentWeekNumber}!\nReasons:\n - ${conflictDetails}\n\nAdd anyway?`);
                    if (!confirmAdd) {
                        console.log("[Display] Add item cancelled due to conflict.");
                        return; // Don't add
                    }
                    console.warn("[Display] User confirmed adding item despite conflict.");
                }
            }

            setBaseScheduleItems(prevItems => {
                const updatedItems = [...prevItems, newEntry];
                // Recalculate conflicts for the new entry and affected entries
                const currentWeekNumber = getWeekNumber(currentDate);
                const currentYear = currentDate.getFullYear();
                conflictManager.recalculateConflictsForChangedEntries(
                    [newEntry.id],
                    updatedItems,
                    teachers,
                    groups,
                    subjects
                ).then(entriesWithUpdatedConflicts => {
                    setBaseScheduleItems(entriesWithUpdatedConflicts);
                });
                return updatedItems;
            });
            addLogEntry('create', null, newEntry);

            // Show notification based on conflict status
            const addConflictStatus = getConflictStatusForEntry(newEntry);
            if (addConflictStatus === 'red' || addConflictStatus === 'orange') {
                showWarning('Created with warnings');
            } else {
                showSuccess('Created successfully');
            }
        } else {
            // Check if this is an exception removal
            if (editedDataObj._removalContext && editedDataObj._removalContext.isExceptionRemoval) {
                const { removedWeek, removedYear } = editedDataObj._removalContext;
                console.log('[ScheduleDisplay] Handling exception removal for week:', removedWeek);

                // Remove the removal context before creating the entry
                const cleanData = { ...editedDataObj };
                delete cleanData._removalContext;

                const editedEntry = new ScheduleEntry(cleanData);

                setBaseScheduleItems(prevItems =>
                    prevItems.map(item => item && item.id === originalItem.id ? editedEntry : item)
                );

                // Add log entry for exception removal (will be detected as 'modify' with fewer exceptions)
                addLogEntry('modify', originalItem, editedEntry);
                showSuccess(`Exception removed for week ${removedWeek}`);

            } else {
                // This is a regular edit operation - the modal has already handled exceptions internally
                const editedEntry = editedDataObj instanceof ScheduleEntry ? editedDataObj : new ScheduleEntry(editedDataObj);

                // Optional: Re-check availability before saving edit
                const startMinutes = editedEntry.startMinutes;
                if (startMinutes !== null) {
                    const currentWeekNumber = getWeekNumber(currentDate);
                    const editAvailability = availabilityService.checkAvailability(
                        editedEntry, editedEntry.room, editedEntry.day, startMinutes,
                        baseScheduleItems, teachers, groups, subjects
                    );
                    if (editAvailability.overallStatus === 'red') {
                        const formattedLines = formatConflictReasonsForEntry(editAvailability, editedEntry);
                        const conflictDetails = formattedLines.join('\n - ');
                        const confirmEdit = window.confirm(`Warning: Saving these edits creates conflicts in week ${currentWeekNumber}!\nReasons:\n - ${conflictDetails}\n\nSave anyway?`);
                        if (!confirmEdit) {
                            console.log("[Display] Edit save cancelled due to conflict.");
                            return; // Don't save
                        }
                        console.warn("[Display] User confirmed saving edit despite conflict.");
                    }
                } else {
                    console.warn("[Display] Could not check availability on edit save due to invalid time.");
                }

                setBaseScheduleItems(prevItems => {
                    const updatedItems = prevItems.map(item => item && item.id === originalItem.id ? editedEntry : item);
                    // Recalculate conflicts for the edited entry and affected entries
                    const currentWeekNumber = getWeekNumber(currentDate);
                    const currentYear = currentDate.getFullYear();
                    conflictManager.recalculateConflictsForChangedEntries(
                        [editedEntry.id],
                        updatedItems,
                        teachers,
                        groups,
                        subjects
                    ).then(entriesWithUpdatedConflicts => {
                        setBaseScheduleItems(entriesWithUpdatedConflicts);
                    });
                    return updatedItems;
                });
                addLogEntry('modify', originalItem, editedEntry);

                // Show notification based on conflict status
                const editConflictStatus = getConflictStatusForEntry(editedEntry);
                if (editConflictStatus === 'red' || editConflictStatus === 'orange') {
                    showWarning('Changes are added to review with warnings');
                } else {
                    showSuccess('Updated successfully');
                }
                showInfo('You need to review them in order to publish them.');
            }
        }

        closeScheduleModal();
    }, [baseScheduleItems, addLogEntry, teachers, groups, subjects, closeScheduleModal, currentDate, showSuccess, showWarning, getConflictStatusForEntry]);

    // Delete handler for removing a specific week from a schedule item
    const handleDeleteForDate = useCallback((scheduleItem, weekToRemove) => {
        console.log('[ScheduleDisplay] Deleting week', weekToRemove, 'from item', scheduleItem.id);

        // Ensure we have a ScheduleEntry instance
        const scheduleEntry = scheduleItem instanceof ScheduleEntry ? scheduleItem : new ScheduleEntry(scheduleItem);
        const targetWeekNumber = parseInt(weekToRemove, 10);
        const targetYear = currentDate.getFullYear();

        // Use ScheduleEntry's removeWeek method
        const updatedEntry = scheduleEntry.removeWeek(targetWeekNumber, targetYear);

        if (updatedEntry === null) {
            // No weeks left, delete the entire item
            setBaseScheduleItems(prevItems =>
                prevItems.filter(item => item.id !== scheduleItem.id)
            );
            addLogEntry('delete', scheduleItem, null);
            showSuccess(`Schedule item deleted completely (no remaining weeks)`);
        } else if (updatedEntry === scheduleEntry) {
            // Entry unchanged (week wasn't part of the series)
            showError(`Week ${weekToRemove} is not part of this schedule item`);
        } else {
            // Update the item with the new week series
            setBaseScheduleItems(prevItems =>
                prevItems.map(item => item.id === scheduleItem.id ? updatedEntry : item)
            );
            addLogEntry('modify', scheduleItem, updatedEntry);
            showSuccess(`Week ${weekToRemove} removed from schedule item`);
        }
    }, [addLogEntry, showSuccess, showError, currentDate]);

    // Delete handler for removing an entire schedule item
    const handleDeleteEntry = useCallback((scheduleItem) => {
        console.log('[ScheduleDisplay] Deleting entire entry', scheduleItem.id);

        setBaseScheduleItems(prevItems =>
            prevItems.filter(item => item.id !== scheduleItem.id)
        );
        addLogEntry('delete', scheduleItem, null);
        showSuccess(`Schedule item deleted successfully`);
    }, [addLogEntry, showSuccess]);

    // Main delete handler that opens confirmation modal
    const handleDeleteClick = useCallback((scheduleItem) => {
        setItemToDelete(scheduleItem);
        setShowDeleteConfirmation(true);
    }, []);

    // Handle delete for current week only
    const handleDeleteForCurrentWeek = useCallback(() => {
        if (!itemToDelete) return;

        const currentWeekNumber = getWeekNumber(currentDate);
        handleDeleteForDate(itemToDelete, currentWeekNumber.toString());

        setShowDeleteConfirmation(false);
        setItemToDelete(null);
        closeScheduleModal();
    }, [itemToDelete, currentDate, handleDeleteForDate]);

    // Handle delete entire entry
    const handleDeleteEntireEntry = useCallback(() => {
        if (!itemToDelete) return;

        const confirmDelete = window.confirm(
            `Are you sure you want to delete this schedule item completely?\n\n` +
            `${itemToDelete.course || itemToDelete.title || 'Untitled Item'}\n` +
            `${itemToDelete.day} at ${itemToDelete.timeRangeDisplay ? itemToDelete.timeRangeDisplay() : 'Unknown time'}\n` +
            `Room: ${itemToDelete.room || 'Unknown'}\n\n` +
            `This action cannot be undone.`
        );

        if (!confirmDelete) {
            return;
        }

        handleDeleteEntry(itemToDelete);

        setShowDeleteConfirmation(false);
        setItemToDelete(null);
        closeScheduleModal();
    }, [itemToDelete, handleDeleteEntry]);

    // Handle cancel delete
    const handleCancelDelete = useCallback(() => {
        setShowDeleteConfirmation(false);
        setItemToDelete(null);
    }, []);

    // Helper function to convert HH:MM:SS to HH:MM format
    const convertTimeToHhMm = (timeStr) => {
        if (!timeStr) return timeStr;

        // If already HH:MM format, return as is
        if (/^\d{2}:\d{2}$/.test(timeStr)) {
            return timeStr;
        }

        // If HH:MM:SS format, remove seconds
        if (/^\d{2}:\d{2}:\d{2}$/.test(timeStr)) {
            return timeStr.substring(0, 5); // Remove last 3 characters (":ss")
        }

        return timeStr; // Return original if format doesn't match
    };

    // Helper function for time validation
    const isValidTimeFormat = (timeStr) => {
        if (!timeStr) return true; // Allow empty for display purposes
        const isValidFormat = /^\d{2}:\d{2}(:\d{2})?\s*-\s*\d{2}:\d{2}(:\d{2})?$/.test(timeStr);
        if (!isValidFormat) return false;
        const parts = timeStr.split('-').map(part => part.trim());
        if (parts.length !== 2) return false;
        const [startTimeStr, endTimeStr] = parts;
        const startParts = startTimeStr.split(':');
        const endParts = endTimeStr.split(':');
        if (startParts.length < 2 || endParts.length < 2) return false;
        const startHours = parseInt(startParts[0], 10);
        const startMinutes = parseInt(startParts[1], 10);
        const endHours = parseInt(endParts[0], 10);
        const endMinutes = parseInt(endParts[1], 10);
        if (isNaN(startHours) || isNaN(startMinutes) || isNaN(endHours) || isNaN(endMinutes)) return false;
        const startTotalMinutes = startHours * 60 + startMinutes;
        const endTotalMinutes = endHours * 60 + endMinutes;
        return endTotalMinutes > startTotalMinutes;
    };

    // --- Memoized Lists for Filters ---
    const uniqueTeachersForFilter = useMemo(() => {
        const names = (teachers || [])
            .map(t => (t?.name ?? '').toString().trim())
            .filter(n => n && n.toUpperCase() !== '__PLACEHOLDER__');
        const deduped = Array.from(new Set(names));
        deduped.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        return [''].concat(deduped);
    }, [teachers]);

    const uniqueCoursesForFilter = useMemo(() => {
        const courses = new Set(baseScheduleItems.map(entry => entry?.course).filter(Boolean));
        return [''].concat(Array.from(courses).sort());
    }, [baseScheduleItems]);
    const uniqueGroupsForFilter = useMemo(() => {
        const groupNames = (groups || []).map(group => group.name || group.group_code || group).filter(Boolean);
        return [''].concat(groupNames.sort());
    }, [groups]);

    // Sorted teachers for the Add/Edit Item modal (alphabetical by name)
    const sortedTeachersForModal = useMemo(() => {
        return (teachers || [])
            .filter(t => {
                const name = (t?.name ?? '').toString().trim();
                return name && name.toUpperCase() !== '__PLACEHOLDER__';
            })
            .slice()
            .sort((a, b) => {
                const nameA = a?.name || '';
                const nameB = b?.name || '';
                return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
            });
    }, [teachers]);

    // Rooms filtered for column view when multiple rooms are selected
    const filteredRoomsForColumns = useMemo(() => {
        if (!Array.isArray(rooms)) return [];
        if (complexFilterRooms.length > 1) {
            const selected = new Set((complexFilterRooms || []).map(v => String(v)));
            return rooms.filter(r => {
                const candidates = [
                    r && r.name,
                    r && (r.roomCode || r.room_code),
                    r && (r.id != null ? String(r.id) : null)
                ].filter(Boolean);
                return candidates.some(val => selected.has(String(val)));
            });
        }
        return rooms || [];
    }, [rooms, complexFilterRooms]);

    // Decide when to use the linear filtered view (keep LinearWeekViewDOM when 0 or 1 room selected)
    const useLinearFilteredView = useMemo(() => {
        return isFilteredView && (complexFilterRooms.length <= 1);
    }, [isFilteredView, complexFilterRooms]);

    // Rooms list for fit view (strings); use selected rooms when >1 selected
    const roomsToUseFit = useMemo(() => {
        if (complexFilterRooms.length > 1) {
            return (filteredRoomsForColumns || [])
                .map(r => r?.name || r?.roomCode || r?.room_code || (r?.id != null ? String(r.id) : ''))
                .filter(Boolean);
        }
        return uniqueRoomsForGrid;
    }, [complexFilterRooms, filteredRoomsForColumns, uniqueRoomsForGrid]);

    // --- Fixed Conflict Calculation (No Circular Dependencies) ---
    const [conflictCalculationTimeoutId, setConflictCalculationTimeoutId] = useState(null);
    const [isCalculatingConflicts, setIsCalculatingConflicts] = useState(false);

    // Use a ref to track the last calculated items to prevent unnecessary recalculations
    const lastConflictCalculationRef = useRef(null);

    const calculateAllConflicts = useCallback(async (itemsToCalculate) => {
        if (!isAdmin || isCalculatingConflicts) return;

        const safeScheduleItems = Array.isArray(itemsToCalculate) ? itemsToCalculate : [];
        if (safeScheduleItems.length === 0) {
            setCurrentConflicts([]);
            return;
        }

        // Check if we've already calculated conflicts for these exact items
        const itemsSignature = safeScheduleItems.map(item => `${item.id}-${item.timeStart}-${item.timeEnd}-${item.room}`).join('|');
        if (lastConflictCalculationRef.current === itemsSignature) {
            return; // Skip if already calculated
        }

        setIsCalculatingConflicts(true);
        console.log("Recalculating all conflicts using ConflictManager...");

        try {
            // Use ConflictManager to recalculate conflicts for all entries
            const updatedEntries = await conflictManager.calculateConflictsForEntries(
                safeScheduleItems,
                teachers,
                groups,
                subjects
            );

            // Extract conflicts for the conflict list display
            const conflicts = [];
            updatedEntries.forEach(entry => {
                const overallStatus = entry.getOverallConflictStatus();
                if (overallStatus === 'red' || overallStatus === 'orange') {
                    // Collect all conflict reasons from all weeks with week information
                    const allReasonsWithWeeks = [];

                    if (entry.conflictsByWeek && entry.conflictsByWeek.size > 0) {
                        entry.conflictsByWeek.forEach((weekConflict, weekKey) => {
                            if (weekConflict.reasons && weekConflict.reasons.length > 0) {
                                // Add week information to each reason if not already present
                                const reasonsWithWeek = weekConflict.reasons.map(reason => {
                                    // Check if the reason already includes week information
                                    const hasWeekInfo = /week \d+/i.test(reason.reason) ||
                                        /\(week \d+\)/i.test(reason.reason);

                                    return {
                                        ...reason,
                                        reason: hasWeekInfo
                                            ? reason.reason
                                            : `${reason.reason} (week ${weekConflict.week})`
                                    };
                                });
                                allReasonsWithWeeks.push(...reasonsWithWeek);
                            }
                        });
                    }

                    // If no conflicts found, add a generic reason
                    const finalReasons = allReasonsWithWeeks.length > 0
                        ? allReasonsWithWeeks
                        : [{ reason: `Status: ${overallStatus}`, severity: overallStatus }];

                    conflicts.push({
                        itemId: entry.id,
                        itemName: entry.displayName,
                        day: entry.day,
                        time: entry.timeRangeDisplay(),
                        room: entry.room,
                        reasons: finalReasons
                    });
                }
            });

            console.log("Conflicts found:", conflicts.length);
            setCurrentConflicts(conflicts);

            // Update the base schedule items with new conflict information
            // Use functional update to avoid dependency on baseScheduleItems
            setBaseScheduleItems(prevItems => {
                // Only update if there are actual changes
                const hasChanges = updatedEntries.some((updatedEntry, index) => {
                    const prevEntry = prevItems[index];
                    if (!prevEntry) return true;

                    // Compare using new per-week conflict system
                    const prevOverallStatus = prevEntry.getOverallConflictStatus();
                    const updatedOverallStatus = updatedEntry.getOverallConflictStatus();

                    if (prevOverallStatus !== updatedOverallStatus) return true;

                    // Compare conflict maps sizes
                    const prevConflictsSize = prevEntry.conflictsByWeek ? prevEntry.conflictsByWeek.size : 0;
                    const updatedConflictsSize = updatedEntry.conflictsByWeek ? updatedEntry.conflictsByWeek.size : 0;

                    if (prevConflictsSize !== updatedConflictsSize) return true;

                    // If both have conflicts, compare the conflict data
                    if (updatedConflictsSize > 0 && prevConflictsSize > 0) {
                        const prevConflictsJson = JSON.stringify(Array.from(prevEntry.conflictsByWeek.entries()));
                        const updatedConflictsJson = JSON.stringify(Array.from(updatedEntry.conflictsByWeek.entries()));
                        return prevConflictsJson !== updatedConflictsJson;
                    }

                    return false;
                });

                if (hasChanges) {
                    lastConflictCalculationRef.current = itemsSignature;
                    return updatedEntries;
                } else {
                    return prevItems; // No changes, return previous state
                }
            });

        } catch (error) {
            console.error('Error calculating conflicts:', error);
            setCurrentConflicts([]);
        } finally {
            setIsCalculatingConflicts(false);
        }
    }, [teachers, groups, subjects, targetWeekNumber, targetYear, isAdmin, isCalculatingConflicts]);

    // Debounced effect for conflict calculation - removed circular dependency
    useEffect(() => {
        if (!isAdmin) {
            setCurrentConflicts([]);
            return;
        }

        // Clear any existing timeout
        if (conflictCalculationTimeoutId) {
            clearTimeout(conflictCalculationTimeoutId);
        }

        // Set a new timeout to debounce the calculation
        const timeoutId = setTimeout(() => {
            calculateAllConflicts(baseScheduleItems);
        }, 300); // 300ms debounce

        setConflictCalculationTimeoutId(timeoutId);

        // Cleanup function
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [baseScheduleItems, isAdmin]); // Removed calculateAllConflicts from dependencies

    const toggleConflictList = () => setIsConflictListVisible(prev => !prev);

    // --- Mock Save ---
    const handleRealSave = useCallback(async (currentScheduleItems, changeLogEntries) => {
        console.log('[ScheduleDisplay] Saving changes to API...', changeLogEntries.length, 'changes');

        setIsLoading(true);
        try {
            // Collect IDs of items that have been modified (created or edited)
            const modifiedItemIds = new Set();
            const deletedItemIds = new Set();

            changeLogEntries.forEach(entry => {
                if (entry.action === 'create') {
                    // For creates, use the itemId from the change log entry
                    if (entry.itemId && entry.itemId !== 'N/A') {
                        modifiedItemIds.add(String(entry.itemId));
                    }
                } else if (entry.action === 'modify') {
                    // For modifications, use the itemId from the change log entry
                    if (entry.itemId && entry.itemId !== 'N/A') {
                        modifiedItemIds.add(String(entry.itemId));
                    }
                } else if (entry.action === 'delete') {
                    // For deletes, use the itemId from the change log entry
                    if (entry.itemId && entry.itemId !== 'N/A') {
                        deletedItemIds.add(String(entry.itemId));
                    }
                }
            });

            // Filter current schedule items to only include modified items
            // Note: Deleted items won't be in currentScheduleItems, but their info is in changeLogEntries
            const modifiedItems = currentScheduleItems.filter(item =>
                modifiedItemIds.has(String(item.id))
            );

            console.log('[ScheduleDisplay] Saving only modified items:', {
                totalItemsInState: currentScheduleItems.length,
                modifiedItemIds: Array.from(modifiedItemIds),
                deletedItemIds: Array.from(deletedItemIds),
                modifiedItemsToSave: modifiedItems.length,
                changeLogEntries: changeLogEntries.length,
                changeTypes: changeLogEntries.map(e => e.action).reduce((acc, action) => {
                    acc[action] = (acc[action] || 0) + 1;
                    return acc;
                }, {})
            });

            // Log details of what's being saved
            if (modifiedItems.length > 0) {
                console.log('[ScheduleDisplay] Modified items details:',
                    modifiedItems.map(item => ({
                        id: item.id,
                        course: item.course,
                        day: item.day,
                        timeStart: item.timeStart,
                        room: item.room,
                        isScheduled: item.isScheduled ? item.isScheduled() : 'unknown'
                    }))
                );
            }

            if (deletedItemIds.size > 0) {
                console.log('[ScheduleDisplay] Deleted items details:',
                    changeLogEntries
                        .filter(entry => entry.action === 'delete')
                        .map(entry => ({
                            id: entry.itemId,
                            details: entry.details,
                            timestamp: entry.timestamp
                        }))
                );
            }

            // Save only the modified items to the backend using AssetManager
            // The changeLogEntries parameter will handle deletions properly
            await assetManager.saveCurrentScheduleState(modifiedItems, null, changeLogEntries);

            console.log('[ScheduleDisplay] Changes saved successfully to live schedule', {
                modifiedItemsSaved: modifiedItems.length,
                deletionsProcessed: deletedItemIds.size
            });

            // Show success message with actual number of changes
            const totalChanges = modifiedItems.length + deletedItemIds.size;
            showSuccess(`${totalChanges} change${totalChanges !== 1 ? 's' : ''} saved successfully to live schedule!`);

            // Update the initial state to reflect the saved changes
            setInitialScheduleItems([...currentScheduleItems]);

        } catch (error) {
            console.error('[ScheduleDisplay] Error saving changes:', error);
            showError("Save failed: " + error.message);
            throw error; // Re-throw to let the change tracking hook handle it
        } finally {
            setIsLoading(false);
        }
    }, [assetManager, showSuccess, showError]);

    const saveChanges = useCallback(async () => {
        try {
            const changes = Array.isArray(changeLog) ? changeLog : getNetChanges(baseScheduleItems);
            await handleRealSave(baseScheduleItems, changes);
            // Reset baseline in change tracking to the newly saved state
            initializeChangeTracking(baseScheduleItems);
            console.log("Changes saved successfully! Change log will reset.");
        } catch (error) {
            console.error('Save failed:', error);
            throw error;
        }
    }, [baseScheduleItems, changeLog, getNetChanges, handleRealSave, initializeChangeTracking]);

    const handleMockSave = () => {
        console.log("Mock Save clicked. Persisting changes and resetting initial state.");
        saveChanges();
    };




    // --- Change ID counts for FloatingActionButtons (to enable Review button) ---
    const changeIdCounts = useMemo(() => {
        try {
            const counts = { created: 0, modified: 0, deleted: 0 };
            const log = Array.isArray(changeLog) ? changeLog : [];
            log.forEach(entry => {
                const action = entry?.action;
                if (action === 'create') counts.created++;
                else if (action === 'modify') counts.modified++;
                else if (action === 'delete') counts.deleted++;
            });
            return counts;
        } catch (e) {
            return { created: 0, modified: 0, deleted: 0 };
        }
    }, [changeLog]);

    const hasPendingChangesByIdSets = useMemo(() => {
        return (changeIdCounts.created + changeIdCounts.modified + changeIdCounts.deleted) > 0;
    }, [changeIdCounts]);

    // --- Render Helpers ---

    const handleCalendarIconClick = useCallback(() => {
        // This can be used for additional logic if needed
        console.log('[ScheduleDisplay] Calendar icon clicked');
    }, []);

    const handleWeekSelect = useCallback((selectedDate) => {
        console.log('[ScheduleDisplay] Week selected:', selectedDate);
        setCurrentDate(selectedDate);
    }, []);

    const reviewButtonPositionClass = useMemo(() => {
        return currentConflicts.length > 0
            ? 'fixed bottom-4 right-16 z-50' // Position left of indicator
            : 'fixed bottom-4 right-4 z-50';
    }, [currentConflicts.length]);

    // Period navigation helpers for Semester mode
    const currentPeriodIndex = useMemo(() => {
        if (!periods || periods.length === 0 || !selectedPeriodId) return -1;
        return periods.findIndex(p => String(p.id) === String(selectedPeriodId));
    }, [periods, selectedPeriodId]);

    const handlePrevPeriod = useCallback(() => {
        if (!periods || periods.length === 0) return;
        if (currentPeriodIndex > 0) {
            setSelectedPeriodId(periods[currentPeriodIndex - 1].id);
        }
    }, [periods, currentPeriodIndex]);

    const handleNextPeriod = useCallback(() => {
        if (!periods || periods.length === 0) return;
        if (currentPeriodIndex >= 0 && currentPeriodIndex < periods.length - 1) {
            setSelectedPeriodId(periods[currentPeriodIndex + 1].id);
        }
    }, [periods, currentPeriodIndex]);

    const currentPeriodLabel = useMemo(() => {
        if (!selectedPeriod) return 'No semester';
        if (typeof selectedPeriod.getDisplayString === 'function') return selectedPeriod.getDisplayString();
        return `${selectedPeriod.name} (Week ${selectedPeriod.start_week}-${selectedPeriod.end_week})`;
    }, [selectedPeriod]);

    const handlePrevWeek = useCallback(() => {
        setCurrentDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setDate(prevDate.getDate() - 7);
            console.log("[ScheduleDisplay] Navigating to Previous Week, New Date:", newDate);
            return newDate;
        });
    }, []);

    const handleNextWeek = useCallback(() => {
        setCurrentDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setDate(prevDate.getDate() + 7);
            console.log("[ScheduleDisplay] Navigating to Next Week, New Date:", newDate);
            return newDate;
        });
    }, []);

    // Handle calendar sub-mode changes (weeks/semester), track last non-occupancy
    const handleCalendarModeChange = useCallback((mode) => {
        if (mode !== 'occupancy') {
            setLastCalendarMode(mode);
        }
        setTimeMode(mode);
        if (hasUnsavedChanges) {
            showWarning('You have unsaved changes. Reloading or leaving the page will discard them.');
        }
    }, [hasUnsavedChanges, showWarning]);

    // Left drawer handlers
    const openLeftDrawer = useCallback(() => {
        setIsLeftDrawerOpen(true);
        // Start animation on next frame to ensure initial off-screen state is applied
        requestAnimationFrame(() => setIsLeftDrawerAnimating(true));
    }, []);
    const closeLeftDrawer = useCallback(() => {
        setIsLeftDrawerAnimating(false);
        // Wait for transition to finish before unmounting
        setTimeout(() => setIsLeftDrawerOpen(false), 300);
    }, []);
    const handleSelectPrimaryView = useCallback((view) => {
        if (view === 'calendar') {
            setTimeMode(prev => (lastCalendarMode || 'weeks'));
        } else if (view === 'occupancy') {
            setTimeMode('occupancy');
        }
        closeLeftDrawer();
        if (hasUnsavedChanges) {
            showWarning('You have unsaved changes. Reloading or leaving the page will discard them.');
        }
    }, [lastCalendarMode, closeLeftDrawer, hasUnsavedChanges, showWarning]);

    // --- Loading State ---
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen bg-white">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading schedule...</p>
                </div>
            </div>
        );
    }

    // --- Error State ---
    if (error) {
        return (
            <div className="flex items-center justify-center h-screen bg-white">
                <div className="text-center">
                    <p className="text-red-500 mb-4">{error}</p>
                    <Button variant="primary" onClick={() => window.location.reload()}>Retry</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="relative flex flex-col h-screen p-4 md:p-6 lg:p-4 bg-gray-50 overflow-hidden">
            {/* Sticky Header */}
            <div className="flex-shrink-0 bg-gray-50 z-40 shadow-sm pb-2">
                {/* Desktop Layout: School Header, Filters, and Time Mode/Week Nav in same row */}
                <div className="hidden md:flex items-center justify-between gap-4 mb-2">
                    {/* Left: Drawer toggle + School Header */}
                    <div className="flex items-center flex-shrink-0">
                        <Button
                            variant="ghost"
                            onClick={openLeftDrawer}
                            className="p-2 rounded-md text-gray-700 hover:text-blue-600"
                            title="Open view menu"
                            aria-label="Open view menu"
                        >
                            {/* Hamburger icon */}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 5h14a1 1 0 110 2H3a1 1 0 110-2zm0 4h14a1 1 0 110 2H3a1 1 0 110-2zm0 4h14a1 1 0 110 2H3a1 1 0 110-2z" clipRule="evenodd" />
                            </svg>
                        </Button>
                        <SchoolHeader />
                    </div>
                    {/* Filters in the middle (hidden in occupancy view) */}
                    {timeMode !== 'occupancy' && (
                        <div className={isPublicView ? "flex-shrink-0 max-w-[500px]" : "flex-1"}>
                            <ScheduleFilter
                                teachers={uniqueTeachersForFilter} courses={uniqueCoursesForFilter} groups={uniqueGroupsForFilter}
                                selectedTeacher={selectedTeacher} selectedCourse={selectedCourse} selectedGroups={selectedGroups}
                                onTeacherChange={handleTeacherChange} onCourseChange={handleCourseChange} onGroupChange={handleGroupChange}
                                onClearFilters={clearFilters}
                                isPublicView={isPublicView}
                                onOpenComplexFilter={handleOpenComplexFilter}
                                complexFilterGroups={complexFilterGroups}
                                complexFilterTeachers={complexFilterTeachers}
                                complexFilterSubjects={complexFilterSubjects}
                                complexFilterRooms={complexFilterRooms}
                                showTeacher={process.env.REACT_APP_TYPE !== 'STUDENTS'}
                                showCourse={false}
                                showGroups={process.env.REACT_APP_TYPE !== 'TEACHERS'}
                            />
                        </div>
                    )}
                    {/* Unified WeekNavigator with time mode + period nav inside */}
                    <div className="flex-shrink-0">
                        <WeekNavigator
                            currentWeekLabel={currentWeekLabel}
                            onCalendarIconClick={handleCalendarIconClick}
                            onPrevWeek={handlePrevWeek}
                            onNextWeek={handleNextWeek}
                            currentDate={currentDate}
                            onWeekSelect={handleWeekSelect}
                            timeMode={timeMode}
                            onTimeModeChange={handleCalendarModeChange}
                            currentPeriodLabel={currentPeriodLabel}
                            currentPeriodIndex={currentPeriodIndex}
                            totalPeriods={periods?.length || 0}
                            onPrevPeriod={handlePrevPeriod}
                            onNextPeriod={handleNextPeriod}
                        />
                    </div>
                </div>

                {/* Mobile Layout: Stacked layout */}
                <div className="block md:hidden space-y-3 mb-2">
                    {/* School Header with Drawer toggle and mobile filter on the right */}
                    <div className="flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                onClick={openLeftDrawer}
                                className="p-2 rounded-md text-gray-700 hover:text-blue-600"
                                title="Open view menu"
                                aria-label="Open view menu"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3 5h14a1 1 0 110 2H3a1 1 0 110-2zm0 4h14a1 1 0 110 2H3a1 1 0 110-2zm0 4h14a1 1 0 110 2H3a1 1 0 110-2z" clipRule="evenodd" />
                                </svg>
                            </Button>
                            <SchoolHeader />
                        </div>
                        {timeMode !== 'occupancy' && (
                            <div className="ml-2">
                                <ScheduleFilter
                                    teachers={uniqueTeachersForFilter}
                                    courses={uniqueCoursesForFilter}
                                    groups={uniqueGroupsForFilter}
                                    selectedTeacher={selectedTeacher}
                                    selectedCourse={selectedCourse}
                                    selectedGroups={selectedGroups}
                                    onTeacherChange={handleTeacherChange}
                                    onCourseChange={handleCourseChange}
                                    onGroupChange={handleGroupChange}
                                    onClearFilters={clearFilters}
                                    isPublicView={isPublicView}
                                    onOpenComplexFilter={handleOpenComplexFilter}
                                    complexFilterGroups={complexFilterGroups}
                                    complexFilterTeachers={complexFilterTeachers}
                                    complexFilterSubjects={complexFilterSubjects}
                                    complexFilterRooms={complexFilterRooms}
                                    showTeacher={!isStudentsBuild()}
                                    showCourse={false}
                                    showGroups={!isTeachersBuild()}
                                    compactMobile={true}
                                />
                            </div>
                        )}
                    </div>

                    {/* Unified WeekNavigator (mobile) */}
                    <div className="flex justify-center">
                        <WeekNavigator
                            currentWeekLabel={currentWeekLabel}
                            onCalendarIconClick={handleCalendarIconClick}
                            onPrevWeek={handlePrevWeek}
                            onNextWeek={handleNextWeek}
                            currentDate={currentDate}
                            onWeekSelect={handleWeekSelect}
                            timeMode={timeMode}
                            onTimeModeChange={handleCalendarModeChange}
                            currentPeriodLabel={currentPeriodLabel}
                            currentPeriodIndex={currentPeriodIndex}
                            totalPeriods={periods?.length || 0}
                            onPrevPeriod={handlePrevPeriod}
                            onNextPeriod={handleNextPeriod}
                        />
                    </div>
                </div>

                {/* Fixed Day Header */}
                <FixedDayHeader
                    currentVisibleDay={currentVisibleDay}
                    currentDate={currentDate}
                    animationDirection={getAnimationDirection()}
                    show={viewMode === 'wide' && timeMode !== 'occupancy' && !useLinearFilteredView}
                />
            </div>

            {/* Scrollable Content */}
            <div ref={scrollContainerRef} className="flex-grow overflow-y-auto">
                <div className={`flex flex-col lg:flex-row gap-6 min-h-0 overflow-hidden ${timeMode === 'occupancy' ? 'max-h-full' : ''}`}>
                    {/* Schedule Area */}
                    <div className="flex-grow min-w-0 overflow-hidden min-h-0">
                        {timeMode === 'occupancy' ? (
                            <RoomsOccupancyView
                                rooms={rooms || []}
                                scheduleEntries={baseScheduleItems || []}
                                selectedPeriod={selectedPeriod}
                                activeDays={activeDays}
                                holidays={weekHolidays}
                                onRoomClick={(room) => console.log('Room clicked:', room)}
                                onTimeSlotClick={(entry, room, dayIndex) => openScheduleModal(entry)}
                            />
                        ) : useLinearFilteredView ? (
                            <LinearWeekViewDOM
                                items={filteredScheduleItems}
                                days={activeDays}
                                onOpenModal={openScheduleModal}
                                user={user}
                                currentWeekStart={getWeekStartDate(currentDate)}
                                selectedTeacher={selectedTeacher}
                                selectedGroups={selectedGroups}
                            />
                        ) : (
                            viewMode === 'wide' ? (
                                <SharedScrollProvider>
                                    <div className="space-y-6 pr-4">
                                        {activeDays.map(day => {
                                            const itemsForDay = filteredScheduleItems.filter(item => item && item.day === day);

                                            // Use time-mode base entries instead of recalculating from base
                                            const dayScheduleEntry = (timeMode === 'semester' && selectedPeriod
                                                ? semesterEntries
                                                : weekActiveEntries).filter(entry => {
                                                    if (!entry || entry.day !== day) return false;

                                                    // Already filtered for current mode
                                                    return true;
                                                });

                                            return (
                                                <div key={`${day}-wide-container`} className="w-full overflow-hidden">
                                                    <DayTableSimple
                                                        key={`${day}-wide`}
                                                        day={day}
                                                        rooms={(complexFilterRooms.length > 1 ? filteredRoomsForColumns : (rooms || []))}
                                                        items={itemsForDay}
                                                        dayScheduleEntry={dayScheduleEntry}
                                                        user={user}
                                                        teachers={teachers}
                                                        groups={groups}
                                                        subjects={subjects}
                                                        onOpenModal={openScheduleModal}
                                                        onItemDrop={handleItemDrop}
                                                        onItemResize={handleItemResize}
                                                        draggedItemInfo={draggedItemInfo}
                                                        onGlobalDragStart={handleGlobalDragStart}
                                                        onGlobalDragEnd={handleGlobalDragEnd}
                                                        availabilityOverlayData={availabilityOverlayData}
                                                        KEY_SEPARATOR={KEY_SEPARATOR}
                                                        dayHeaderRef={element => dayHeaderRefs.current[day] = element}
                                                        currentWeekNumber={targetWeekNumber}
                                                        currentYear={targetYear}
                                                        dragToCreateData={dragToCreateData}
                                                        onDragToCreateStart={handleDragToCreateStart}
                                                        onDragToCreateMove={handleDragToCreateMove}
                                                        onDragToCreateEnd={handleDragToCreateEnd}
                                                        onDragToCreateCancel={handleDragToCreateCancel}
                                                        onDuplicateItem={handleDuplicateItem}
                                                        onDelete={handleDeleteItem}
                                                        isPublicView={isPublicView}
                                                        holiday={weekHolidays.get(day) || null}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </SharedScrollProvider>
                            ) : (
                                <SharedScrollProvider>
                                    <div ref={fitViewContainerRef} className="flex flex-col w-full border border-gray-300 bg-white shadow-lg" style={{ height: `${fitViewHeight}px` }}>
                                        {activeDays.map((day, index) => {
                                            const itemsForDay = filteredScheduleItems.filter(item => item && item.day === day);
                                            const dayTableFitHeaderHeight = 40; // Approx header height in DayTableFit
                                            const contentHeightForChild = Math.max(1, fitViewHeight / activeDays.length - dayTableFitHeaderHeight);

                                            // Use time-mode base entries instead of recalculating from base
                                            const dayScheduleEntry = (timeMode === 'semester' && selectedPeriod
                                                ? semesterEntries
                                                : weekActiveEntries).filter(entry => {
                                                    if (!entry || entry.day !== day) return false;

                                                    // Already filtered for current mode
                                                    return true;
                                                });

                                            return (
                                                <div key={`${day}-fit-wrapper`} className={`flex-grow ${index < activeDays.length - 1 ? 'border-b border-gray-200' : ''}`} style={{ minHeight: 0 }}>
                                                    <DayTableFit
                                                        key={`${day}-fit`} day={day} rooms={roomsToUseFit} items={itemsForDay}
                                                        dayScheduleEntry={dayScheduleEntry} user={user}
                                                        teachers={teachers}
                                                        groups={groups}
                                                        subjects={subjects}
                                                        currentWeekNumber={targetWeekNumber}
                                                        currentYear={targetYear}
                                                        onOpenModal={openScheduleModal}
                                                        availableContentHeight={contentHeightForChild}
                                                        onItemDrop={handleItemDrop}
                                                        draggedItemInfo={draggedItemInfo}
                                                        onGlobalDragStart={handleGlobalDragStart}
                                                        onGlobalDragEnd={handleGlobalDragEnd}
                                                        availabilityOverlayData={availabilityOverlayData}
                                                        KEY_SEPARATOR={KEY_SEPARATOR}
                                                        dragToCreateData={dragToCreateData}
                                                        onDragToCreateStart={handleDragToCreateStart}
                                                        onDragToCreateMove={handleDragToCreateMove}
                                                        onDragToCreateEnd={handleDragToCreateEnd}
                                                        onDragToCreateCancel={handleDragToCreateCancel}
                                                        isPublicView={isPublicView}
                                                        holiday={weekHolidays.get(day) || null}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </SharedScrollProvider>
                            )
                        )}
                    </div>

                    {/* Events Panel - hidden in occupancy mode */}
                    {timeMode !== 'occupancy' && (
                        <EventContainer
                            baseScheduleItems={baseScheduleItems}
                            currentDate={currentDate}
                            isAdmin={isAdmin}
                            isPublicView={isPublicView}
                            onOpenScheduleModal={openScheduleModal}
                            groups={groups}
                            subjects={subjects}
                            teachers={teachers}
                            rooms={rooms}
                            activeDays={activeDays}
                        />
                    )}
                </div>
            </div>

            {/* Floating Action Buttons (FAB) - Only show when not in filtered view, not on mobile, and not in occupancy */}
            {!isFilteredView && timeMode !== 'occupancy' && (
                <div className="hidden md:flex fixed top-1/2 right-6 z-50 flex-col space-y-3 transform -translate-y-1/2">
                    <Button
                        onClick={toggleViewMode}
                        variant="secondary" // Or another appropriate variant
                        className="rounded-full p-2 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center w-14 h-14 bg-white text-gray-700 hover:bg-gray-50 ring-gray-400" // Style like other FABs
                        title={viewMode === 'wide' ? 'Switch to Fit View' : 'Switch to Wide View'}
                        aria-label={viewMode === 'wide' ? 'Switch to Fit View' : 'Switch to Wide View'}
                    >
                        {/* Conditionally render the icon based on the CURRENT view */}
                        {viewMode === 'wide' ? <FitIcon /> : <ExpandIcon />}
                    </Button>

                    {/* <Button
                        variant="secondary" onClick={openChangeLogModal} disabled={changeLog.length === 0}
                        className={`rounded-full p-3 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center w-14 h-14 transition-opacity duration-200 ${changeLog.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed ring-gray-300' : 'bg-white text-gray-700 hover:bg-gray-50 ring-gray-400'}`}
                        title={`Review ${changeLog.length} Changes`} aria-label={`Review ${changeLog.length} Changes`}
                    >
                        <SaveIcon />
                        {changeLog.length > 0 && (
                            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">{changeLog.length}</span>
                        )}
                    </Button> */}
                </div>
            )}

            {/* Publish/Conflicts Floating Buttons */}
            {isAdmin && (
                <FloatingActionButtons
                    showSaveDraft={false}
                    showPublishDraft={false}
                    showConflictIndicator={true}
                    showReviewChanges={isAdmin}
                    isLoading={isLoading}
                    changeLog={changeLog}
                    changeLogLength={changeLog.length}
                    currentDraftName={null}
                    isLiveMode={true}
                    currentConflicts={currentConflicts}
                    scheduledInstances={baseScheduleItems}
                    serverBaseline={initialScheduleItems}
                    changeIdCounts={changeIdCounts}
                    hasPendingChangesByIdSets={hasPendingChangesByIdSets}
                    onSaveCurrent={saveChanges}
                    onPublishDraft={saveChanges}
                    onToggleConflictList={toggleConflictList}
                    onSaveChangeLog={saveChanges}
                />
            )}

            {/* --- Modals --- */}
            {/* Left Drawer for selecting primary view */}
            {isLeftDrawerOpen && (
                <div className="fixed inset-0 z-50">
                    <div
                        className={`absolute inset-0 bg-black transition-opacity duration-300 ${isLeftDrawerAnimating ? 'bg-opacity-30 opacity-100' : 'bg-opacity-0 opacity-0'}`}
                        onClick={closeLeftDrawer}
                    ></div>
                    <div className={`absolute top-0 left-0 h-full w-64 bg-white shadow-xl transform transition-transform duration-300 ease-out ${isLeftDrawerAnimating ? 'translate-x-0' : '-translate-x-full'}`}>
                        <div className="p-4 border-b flex items-center justify-between">
                            <span className="text-lg font-semibold text-gray-800">Views</span>
                            <button
                                className="p-1 rounded-md text-gray-600 hover:text-blue-600"
                                onClick={closeLeftDrawer}
                                aria-label="Close menu"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-2 space-y-1">
                            <button
                                className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 ${timeMode !== 'occupancy' ? 'bg-gray-200 text-gray-900' : 'hover:bg-gray-100 text-gray-700'}`}
                                onClick={() => handleSelectPrimaryView('calendar')}
                            >
                                <HiCalendarDays className="h-5 w-5" />
                                <span>Calendar</span>
                            </button>
                            <button
                                className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 ${timeMode === 'occupancy' ? 'bg-gray-200 text-gray-900' : 'hover:bg-gray-100 text-gray-700'}`}
                                onClick={() => handleSelectPrimaryView('occupancy')}
                            >
                                <HiBuildingOffice className="h-5 w-5" />
                                <span>Rooms Occupancy</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Item Modal (admin only) */}
            {isAdmin && (
                <Modal isOpen={isScheduleModalOpen} onClose={closeScheduleModal} title="Add New Schedule Item">
                    <form onSubmit={handleDragToCreateEnd} className="space-y-4">
                        {/* Week Series */}
                        <div>
                            <label htmlFor="add-week-series" className="block text-sm font-medium text-gray-700 mb-1">Week Series</label>
                            <Input
                                id="add-week-series"
                                name="weekSeries"
                                value={selectedScheduleItem?.weekSeries}
                                onChange={(e) => {
                                    setSelectedScheduleItem(prev => ({ ...prev, weekSeries: e.target.value }));
                                }}
                                placeholder="e.g., 38,44 or 52-4"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">Enter comma-separated weeks or week ranges (e.g., "38,44" or "52-4")</p>
                        </div>

                        {/* Year */}
                        <div>
                            <label htmlFor="add-year" className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                            <Input
                                id="add-year"
                                name="year"
                                type="number"
                                value={selectedScheduleItem?.year}
                                onChange={(e) => {
                                    setSelectedScheduleItem(prev => ({ ...prev, year: e.target.value }));
                                }}
                                min={new Date().getFullYear()}
                                max={new Date().getFullYear() + 10}
                                required
                            />
                        </div>

                        {/* Groups (Multiple Select) */}
                        <div>
                            <label htmlFor="add-groups" className="block text-sm font-medium text-gray-700 mb-1">Groups</label>
                            <select
                                id="add-groups"
                                name="groups"
                                multiple
                                value={selectedScheduleItem?.groups}
                                onChange={(e) => {
                                    const selectedGroups = Array.from(e.target.selectedOptions, option => option.value);
                                    setSelectedScheduleItem(prev => ({ ...prev, groups: selectedGroups }));
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                                required
                            >
                                {(groups || []).map(group => (
                                    <option key={group.id} value={group.id}>{group.name}</option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple groups</p>
                        </div>

                        {/* Day */}
                        <div>
                            <label htmlFor="add-day" className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                            <select id="add-day" name="day" value={selectedScheduleItem?.day} onChange={(e) => {
                                setSelectedScheduleItem(prev => ({ ...prev, day: e.target.value }));
                            }} required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm capitalize">
                                {activeDays.map(d => <option key={d} value={d} className="capitalize">{d}</option>)}
                            </select>
                        </div>
                        {/* Time */}
                        <div>
                            <label htmlFor="add-time" className="block text-sm font-medium text-gray-700 mb-1">Time (HH:MM - HH:MM)</label>
                            <Input id="add-time" name="time" value={selectedScheduleItem?.timeRangeDisplay()} onChange={(e) => {
                                let timeValue = e.target.value;

                                // Convert HH:MM:SS format to HH:MM format
                                if (timeValue.includes(' - ')) {
                                    const parts = timeValue.split(' - ');
                                    if (parts.length === 2 && parts[0] && parts[1]) {
                                        const startTime = convertTimeToHhMm(parts[0].trim());
                                        const endTime = convertTimeToHhMm(parts[1].trim());
                                        timeValue = `${startTime} - ${endTime}`;
                                    }
                                } else {
                                    // Handle single time value (for partial input)
                                    timeValue = convertTimeToHhMm(timeValue);
                                }

                                setSelectedScheduleItem(prev => ({ ...prev, time: timeValue }));
                            }} placeholder="e.g., 09:00 - 10:30" required />
                            {!isValidTimeFormat(selectedScheduleItem?.timeRangeDisplay()) && selectedScheduleItem?.timeRangeDisplay() && <p className="text-xs text-red-500 mt-1">Invalid format or end time not after start time.</p>}
                        </div>
                        {/* Course */}
                        <div>
                            <label htmlFor="add-course" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
                            <select id="add-course" name="course" value={selectedScheduleItem?.course} onChange={(e) => {
                                setSelectedScheduleItem(prev => ({ ...prev, course: e.target.value }));
                            }} required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm">
                                <option value="">-- Select Course --</option>
                                {(subjects || []).map(subject => <option key={subject.id} value={subject.name}>{subject.name}</option>)}
                            </select>
                        </div>
                        {/* Teacher */}
                        <div>
                            <label htmlFor="add-teacher" className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
                            <select id="add-teacher" name="teacher" value={selectedScheduleItem?.teacher} onChange={(e) => {
                                setSelectedScheduleItem(prev => ({ ...prev, teacher: e.target.value }));
                            }} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm">
                                <option value="">-- Select Teacher --</option>
                                {(sortedTeachersForModal || []).map(teacher => <option key={teacher.id} value={teacher.name}>{teacher.name}</option>)}
                            </select>
                        </div>
                        {/* Room */}
                        <div>
                            <label htmlFor="add-room" className="block text-sm font-medium text-gray-700 mb-1">Room</label>
                            <select id="add-room" name="room" value={selectedScheduleItem?.room} onChange={(e) => {
                                setSelectedScheduleItem(prev => ({ ...prev, room: e.target.value }));
                            }} required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm">
                                <option value="" disabled>-- Select Room --</option>
                                {(rooms || []).map(room => {
                                    const roomName = room.name || room.room_code || room;
                                    const roomValue = typeof room === 'string' ? room : (room.name || room.room_code);
                                    return <option key={room.id || roomValue} value={roomValue}>{roomName}</option>;
                                })}
                            </select>
                        </div>
                        {/* Item Type */}
                        <div>
                            <label htmlFor="add-type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <select id="add-type" name="type" value={selectedScheduleItem?.type} onChange={(e) => {
                                setSelectedScheduleItem(prev => ({ ...prev, type: e.target.value }));
                            }} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm capitalize">
                                {['class', 'workshop', 'supervision', 'meeting', 'event', 'break'].map(type => <option key={type} value={type} className="capitalize">{type}</option>)}
                            </select>
                        </div>
                        {/* Actions */}
                        <div className="mt-6 flex justify-end space-x-3">
                            <Button type="button" variant="secondary" onClick={closeScheduleModal}>Cancel</Button>
                            <Button type="submit" variant="primary">Add to Review</Button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Public Detail View Modal (shown when public teacher filter is active) */}
            <ScheduleEntryDetailView
                isOpen={isDetailViewOpen}
                onClose={closeDetailView}
                scheduleEntry={selectedScheduleItem}
                groups={groups}
                subjects={subjects}
                teachers={teachers}
                rooms={rooms}
                isPublic={true}
            />

            {/* Edit Item Modal */}
            <EditScheduleItemModal
                isOpen={isScheduleModalOpen}
                onClose={closeScheduleModal}
                selectedScheduleItem={selectedScheduleItem}
                onSaveEdit={handleSaveEdit}
                onDelete={handleDeleteClick}
                groups={groups}
                subjects={subjects}
                teachers={teachers}
                rooms={rooms}
                currentYear={currentDate.getFullYear()}
                currentDate={currentDate}
                isAdmin={isAdmin}
                baseScheduleItems={baseScheduleItems}
                onAddTeacher={onAddTeacher}
                onAddSubject={onAddSubject}
            />

            {/* Change Log Modal removed; Review flow handled by FloatingActionButtons */}

            {/* Conflict List Modal */}
            <ConflictListModal
                isOpen={isConflictListVisible}
                onClose={toggleConflictList}
                conflicts={currentConflicts}
                currentWeekNumber={targetWeekNumber}
                currentYear={targetYear}
            />

            {/* Week Change Confirmation Modal */}
            <WeekChangeConfirmationModal
                isOpen={isWeekConfirmationModalOpen}
                onClose={handleWeekConfirmationClose}
                onConfirm={handleWeekConfirmationConfirm}
                scheduleItem={pendingMoveData?.originalItem}
                currentWeekNumber={pendingMoveData?.currentWeekNumber}
                currentYear={pendingMoveData?.currentYear}
            />

            {/* Complex Filter Modal */}
            <ComplexFilterModal
                isOpen={isComplexFilterModalOpen}
                onClose={handleCloseComplexFilter}
                onApplyFilters={handleApplyComplexFilters}
                groups={groups}
                teachers={teachers}
                subjects={subjects}
                rooms={rooms}
                initialSelectedGroups={complexFilterGroups}
                initialSelectedTeachers={complexFilterTeachers}
                initialSelectedSubjects={complexFilterSubjects}
                initialSelectedRooms={complexFilterRooms}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Schedule Item</h3>
                        <p className="text-sm text-gray-600 mb-6">
                            How would you like to delete this schedule item?
                        </p>

                        <div className="space-y-3">
                            <Button
                                onClick={handleDeleteForCurrentWeek}
                                variant="secondary"
                                className="w-full text-left justify-start"
                            >
                                <div>
                                    <div className="font-medium">Delete for this week only</div>
                                    <div className="text-xs text-gray-500">
                                        Remove this occurrence while keeping the rest of the series (Week {getWeekNumber(currentDate)})
                                    </div>
                                </div>
                            </Button>

                            <Button
                                onClick={handleDeleteEntireEntry}
                                variant="danger"
                                className="w-full text-left justify-start bg-red-600 hover:bg-red-700 text-white"
                            >
                                <div>
                                    <div className="font-medium">Delete entire entry</div>
                                    <div className="text-xs text-red-100">
                                        Remove this schedule item completely
                                    </div>
                                </div>
                            </Button>
                        </div>

                        <div className="mt-6 flex justify-end">
                            <Button variant="secondary" onClick={handleCancelDelete}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Migration Info Modal - public admin build on mobile */}
            {isPublicView && isAdminBuild() && (
                <Modal isOpen={showMigrationModal} onClose={() => setShowMigrationModal(false)} title="We moved to dedicated portals">
                    <div className="space-y-4">
                        <p className="text-sm text-gray-700">
                            For a better experience, public schedules are available on dedicated portals:
                        </p>
                        <div className="flex flex-col items-center gap-2">
                            <a
                                href={studentsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded-md font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-out bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400 flex items-center gap-2 px-4 py-2 text-sm w-full max-w-xs justify-center"
                            >
                                <PiStudentBold className="h-4 w-4" />
                                Open Student Portal
                            </a>
                            <a
                                href={teachersUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded-md font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-out bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400 flex items-center gap-2 px-4 py-2 text-sm w-full max-w-xs justify-center"
                            >
                                <PiChalkboardTeacherBold className="h-4 w-4" />
                                Open Teacher Portal
                            </a>
                        </div>
                    </div>
                </Modal>
            )}


        </div> // End main container div
    );
}

export default ScheduleDisplay;
